import type { SubscriptionType } from '@form-factory/schema';
import {
  Role,
  SubscriptionStatus,
  SubscriptionTier,
} from '@form-factory/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Button, toast } from '@utima/ui';
import { Loader2, ExternalLink } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Card } from '@/components/card/Card';
import { Protected } from '@/components/protected/Protected';
import { AutoRenewalSettings } from '@/features/organizations/components/AutoRenewalSettings';
import { BalanceTopUp } from '@/features/organizations/components/BalanceTopUp';
import { SubscriptionStatusDisplay } from '@/features/organizations/components/SubscriptionStatusDisplay';
import { UsageMetricsDisplay } from '@/features/organizations/components/UsageMetricsDisplay';
import { useSubscriptionManager } from '@/features/organizations/hooks/useSubscriptionManager';
import { trpc } from '@/services/trpc';

export const Route = createFileRoute(
  '/_baseLayout/organizations/$organizationId/payments',
)({
  component: OrganizationsPaymentsPage,
  beforeLoad: async ({
    context: { accessGuard },
    params: { organizationId },
  }) => accessGuard([Role.SuperAdmin, Role.Admin], organizationId),
  loader: async ({ context: { trpcUtils }, params: { organizationId } }) => {
    await Promise.all([
      trpcUtils.subscriptions.getActive.ensureData({ organizationId }),
      trpcUtils.organizations.findOne.ensureData({ id: organizationId }),
    ]);
  },
});

export function OrganizationsPaymentsPage() {
  const { organizationId } = Route.useParams();
  const { t } = useLingui();
  const trpcUtils = trpc.useUtils();

  const [activeSubscriptionData] =
    trpc.subscriptions.getActive.useSuspenseQuery({ organizationId });
  const [organizationData] = trpc.organizations.findOne.useSuspenseQuery({
    id: organizationId,
  });

  const { isLoading: isSubscriptionLoading, createBillingPortal } =
    useSubscriptionManager(organizationId);

  const handleUpdate = () => {
    void trpcUtils.subscriptions.getActive.invalidate({ organizationId });
    void trpcUtils.organizations.findOne.invalidate({ id: organizationId });
  };

  const handleStripePortalClick = async () => {
    if (!organizationData.stripeCustomerId) {
      toast.error(t`Stripe customer ID not found for this organization.`);

      return;
    }
    try {
      const result = await createBillingPortal({
        returnUrl: window.location.href,
      });
      if (result.url) {
        window.location.href = result.url;
      } else {
        toast.error(t`Failed to generate billing portal link.`);
      }
    } catch (error) {
      console.error('Error calling createBillingPortal:', error);
      toast.error(
        t`Failed to open billing portal: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  };

  return (
    <Protected roles={[Role.SuperAdmin, Role.Admin]}>
      <div className='space-y-6 pb-8'>
        <div className='mb-4 flex items-center justify-between'>
          <h2 className='text-2xl font-semibold'>
            <Trans>Billing Overview</Trans>
          </h2>
          {organizationData.stripeCustomerId && (
            <Button
              data-cta='admin-organizations-payments-manage-billing-button'
              variant='secondary'
              onClick={handleStripePortalClick}
              disabled={
                isSubscriptionLoading || !organizationData.stripeCustomerId
              }
              icon={
                isSubscriptionLoading ? (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                ) : (
                  <ExternalLink className='mr-2 size-4' />
                )
              }
            >
              <Trans>Manage Billing & Invoices</Trans>
            </Button>
          )}
        </div>

        {activeSubscriptionData?.status === SubscriptionStatus.Canceled && (
          <Alert
            variant='info'
            title={t`Subscription Canceled`}
            className='mb-6'
          >
            <Trans>
              Your subscription is currently canceled. You can select a new plan
              to reactivate.
            </Trans>
          </Alert>
        )}
        {activeSubscriptionData &&
          (activeSubscriptionData.status === SubscriptionStatus.PastDue ||
            activeSubscriptionData.status === SubscriptionStatus.Unpaid) && (
            <Alert variant='danger' title={t`Payment Issue`} className='mb-6'>
              <Trans>
                There is an issue with your payment method. Please update it via
                the billing portal.
              </Trans>
            </Alert>
          )}

        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
          <div className='space-y-6'>
            <Card title={t`Current Plan`}>
              <SubscriptionStatusDisplay
                organization={organizationData}
                subscription={
                  activeSubscriptionData as Omit<
                    SubscriptionType,
                    'organization'
                  > | null
                }
              />
              <div className='mt-4'>
                <Button
                  data-cta='admin-organizations-payments-change-plan-button'
                  variant='secondary'
                  size='sm'
                  asChild
                >
                  <Link
                    to='/organizations/$organizationId/subscriptions'
                    params={{ organizationId }}
                  >
                    <Trans>Change Plan</Trans>
                  </Link>
                </Button>
              </div>
            </Card>

            <Card title={t`Usage & Balance`}>
              <UsageMetricsDisplay
                organization={organizationData}
                subscription={activeSubscriptionData}
              />
            </Card>
          </div>

          <div className='space-y-6'>
            {activeSubscriptionData &&
              activeSubscriptionData.tier !== SubscriptionTier.Free && (
                <Card title={t`Add Funds`}>
                  <div>
                    <BalanceTopUp
                      organizationId={organizationId}
                      onBalanceAdded={handleUpdate}
                    />
                  </div>
                  <p className='mt-3 text-xs text-gray-500'>
                    <Trans>
                      Add funds to your prepaid balance. This balance does not
                      expire and covers usage beyond your plan&apos;s monthly
                      allowance.
                    </Trans>
                  </p>
                </Card>
              )}

            {activeSubscriptionData &&
              activeSubscriptionData.tier !== SubscriptionTier.Free && (
                <Card title={t`Auto-Renewal Settings`}>
                  <AutoRenewalSettings
                    subscription={
                      activeSubscriptionData as unknown as SubscriptionType
                    }
                    currency={organizationData.currency}
                    organizationId={organizationId}
                    onUpdate={handleUpdate}
                  />
                </Card>
              )}
          </div>
        </div>
      </div>
    </Protected>
  );
}
